import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCopy,
  faTimes,
  faLink,
} from "@fortawesome/pro-light-svg-icons";

import Button from "components/Button";
import { useUnmounted } from "utils/hooks";

const copyToClipboard = (text: string): Promise<void> => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  }
  // navigator.clipboard is only available in secure contexts
  // use the legacy document.execCommand("copy") as a fallback
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand("copy");
    return Promise.resolve();
  } catch (err) {
    return Promise.reject(err);
  } finally {
    document.body.removeChild(textarea);
  }
};

export type Props = {
  className?: string;
  content: string | (() => string);
  label?: string;
  title?: string;
  fixedWidth?: boolean;
  variant?: "text" | "link";
};

const CopyToClipboard: React.FunctionComponent<Props> = ({
  className,
  content,
  label,
  title,
  fixedWidth,
  variant,
}) => {
  const unmounted = useUnmounted();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(null);

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const actualContent = typeof content === "function" ? content() : content;
    copyToClipboard(actualContent)
      .then(() => {
        if (unmounted.current) {
          return;
        }

        setCopied(true);
      })
      .catch((err: Error) => {
        setCopyError(err.message);
      })
      .finally(() => {
        if (unmounted.current) {
          return;
        }

        setTimeout(() => {
          if (unmounted.current) {
            return;
          }

          setCopied(null);
        }, 2000);
      });
  };

  let copyIcon;
  if (copyError) {
    copyIcon = (
      <FontAwesomeIcon
        icon={faTimes}
        title={copyError}
        fixedWidth={fixedWidth}
      />
    );
  } else if (copied) {
    copyIcon = (
      <FontAwesomeIcon icon={faCheck} title="Copied!" fixedWidth={fixedWidth} />
    );
  } else {
    copyIcon = (
      <FontAwesomeIcon
        icon={variant === "link" ? faLink : faCopy}
        title={title}
        fixedWidth={fixedWidth}
      />
    );
  }

  return (
    <Button onClick={handleCopy} className={className} variant="link" bare>
      {copyIcon}
      {label && <> {label}</>}
    </Button>
  );
};

export default CopyToClipboard;
